import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faSpinner,
  faFolder,
  faDownload,
  faStop,
  faCheckCircle,
  faTimesCircle,
  faExclamationTriangle,
  faPlay,
  faFileExcel,
  faFilePdf
} from '@fortawesome/free-solid-svg-icons';
import { api, getApiUrl } from '../utils/apiConfig';
import { useAccount } from '../contexts/AccountContext';
import { getUserData } from '../utils/userUtils';
import StatusBadge from './StatusBadge';
import './CampaignQADetails.css';

const POLLING_INTERVAL = 15000; // 15 seconds
const POLLING_TIMEOUT = 20 * 60 * 1000; // 20 minutes max polling time

/**
 * CampaignQADetails component for viewing QA validation results
 */
const CampaignQADetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAccountIdToUse } = useAccount();
  const [user] = useState(() => getUserData());
  const accountId = getAccountIdToUse();

  const [jobData, setJobData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPolling, setIsPolling] = useState(true);
  const [pollingTimedOut, setPollingTimedOut] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const pollingTimeoutRef = useRef(null);
  const pollingStartTimeRef = useRef(null);

  const fetchJobDetails = useCallback(async () => {
    if (!accountId || !id) return;
    try {
      const response = await api.get(getApiUrl(`/api/v2/campaign-qa/${id}`), {
        params: { accountId, userId: user?.userId || user?.id }
      });
      if (response.status === 200) {
        setJobData(response.data);
        setError(null);

        // Stop polling if job is in terminal state
        const status = response.data.status;
        if (status === 'completed' || status === 'failed' || status === 'stopped') {
          setIsPolling(false);
        }
        // Keep polling for parsing and processing states
        if (status === 'parsing' || status === 'processing') {
          setIsPolling(true);
        }
      }
    } catch (err) {
      console.error('Error fetching job details:', err);
      setError(err.response?.data?.error || 'Failed to load job details');
    } finally {
      setIsLoading(false);
    }
  }, [accountId, id, user]);

  useEffect(() => { fetchJobDetails(); }, [fetchJobDetails]);

  // Set polling start time when component mounts or job starts processing
  useEffect(() => {
    if (isPolling && !pollingStartTimeRef.current) {
      pollingStartTimeRef.current = Date.now();
    }
  }, [isPolling]);

  useEffect(() => {
    if (isPolling && !isLoading) {
      // Check if polling has timed out
      if (pollingStartTimeRef.current && (Date.now() - pollingStartTimeRef.current) > POLLING_TIMEOUT) {
        setIsPolling(false);
        setPollingTimedOut(true);
        console.log('Polling timed out after 20 minutes');
        return;
      }
      pollingTimeoutRef.current = setTimeout(() => fetchJobDetails(), POLLING_INTERVAL);
    }
    return () => { if (pollingTimeoutRef.current) clearTimeout(pollingTimeoutRef.current); };
  }, [isPolling, isLoading, fetchJobDetails, jobData]);

  const handleRunValidation = async () => {
    try {
      setIsRunning(true);
      await api.post(getApiUrl(`/api/v2/campaign-qa/${id}/run`), {}, {
        params: { accountId, userId: user?.userId || user?.id }
      });
      setIsPolling(true);
      pollingStartTimeRef.current = Date.now();
      fetchJobDetails();
    } catch (err) {
      console.error('Error running validation:', err);
      setError(err.response?.data?.error || 'Failed to run validation');
    } finally {
      setIsRunning(false);
    }
  };

  const handleDownloadExcel = () => {
    if (jobData?.excelFileUrl) {
      const link = document.createElement('a');
      link.href = jobData.excelFileUrl;
      link.download = jobData.excelFileName || 'template.xlsx';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleString();
  };

  // Loading state
  if (isLoading && !jobData) {
    return (
      <div className="qa-details-loading">
        <FontAwesomeIcon icon={faSpinner} spin size="2x" />
        <p>Loading job details...</p>
      </div>
    );
  }

  // Error state
  if (error && !jobData) {
    return (
      <div className="qa-details-error">
        <p>{error}</p>
        <button onClick={() => navigate('/campaign-qa')}>Back to Campaign QA</button>
      </div>
    );
  }

  if (!jobData) return null;

  const discrepanciesRaw = jobData.discrepancies || [];
  const severityOrder = { high: 0, medium: 1, low: 2 };
  const discrepancies = [...discrepanciesRaw].sort((a, b) =>
    (severityOrder[a.severity?.toLowerCase()] ?? 3) - (severityOrder[b.severity?.toLowerCase()] ?? 3)
  );

  return (
    <div className="qa-details-container">
      {/* Header */}
      <div className="qa-details-header">
        <Link to="/campaign-qa" className="back-link">
          <FontAwesomeIcon icon={faArrowLeft} /> Back to Campaign QA
        </Link>
        <div className="header-content">
          <div className="header-title-row">
            <h1>{jobData.name}</h1>
            <StatusBadge status={jobData.status} />
          </div>
          <div className="header-meta">
            <span className="meta-item">
              <FontAwesomeIcon icon={faFolder} /> {jobData.folderName || 'No folder'}
            </span>
            <span className="meta-item">Created: {formatDate(jobData.createdAt)}</span>
            <span className="meta-item">Platform: {jobData.platform === 'dv360' ? 'DV360' : jobData.platform === 'facebook' ? 'Facebook' : jobData.platform}</span>
          </div>
          <div className="header-meta">
            {jobData.advertiserId && <span className="meta-item">Advertiser ID: {jobData.advertiserId}</span>}
            <span className="meta-item">Campaign ID: {jobData.campaignId}</span>
          </div>
        </div>
      </div>

      {/* Actions Bar */}
      <div className="qa-actions-bar">
        {jobData.excelFileUrl && (
          <button className="btn btn-secondary" onClick={handleDownloadExcel}>
            <FontAwesomeIcon icon={jobData.platform === 'facebook' ? faFilePdf : faFileExcel} />
            {jobData.platform === 'facebook' ? ' Download IO' : ' Download Template'}
          </button>
        )}
        {(jobData.status === 'pending' || jobData.status === 'failed' || jobData.status === 'stopped') && (
          <button className="btn btn-primary" onClick={handleRunValidation} disabled={isRunning}>
            <FontAwesomeIcon icon={isRunning ? faSpinner : faPlay} spin={isRunning} />
            {isRunning ? 'Running...' : 'Run Validation'}
          </button>
        )}
        {isPolling && (jobData.status === 'processing' || jobData.status === 'parsing') && (
          <span className="polling-indicator">
            <FontAwesomeIcon icon={faSpinner} spin /> Auto-refreshing every 15s
            <button
              className="stop-polling-btn"
              onClick={async () => {
                try {
                  await api.post(getApiUrl(`/api/v2/campaign-qa/${id}/cancel`), {}, {
                    params: { accountId, userId: user?.userId || user?.id }
                  });
                  setIsPolling(false);
                  if (pollingTimeoutRef.current) {
                    clearTimeout(pollingTimeoutRef.current);
                    pollingTimeoutRef.current = null;
                  }
                  fetchJobDetails();
                } catch (err) {
                  console.error('Error cancelling job:', err);
                }
              }}
              title="Stop auto-refresh"
            >
              <FontAwesomeIcon icon={faStop} /> Stop
            </button>
          </span>
        )}
        {pollingTimedOut && (jobData.status === 'processing' || jobData.status === 'parsing') && (
          <span className="polling-timeout">
            Timed out - <button onClick={fetchJobDetails} className="refresh-btn">Refresh</button>
          </span>
        )}
      </div>

      {/* Validation Summary - only show for completed jobs */}
      {jobData.status === 'completed' && (
        <div className="qa-summary-section">
          <h2>Validation Summary</h2>
          <div className={`validation-result-card ${jobData.validationPassed ? 'passed' : 'failed'}`}>
            <FontAwesomeIcon icon={jobData.validationPassed ? faCheckCircle : faTimesCircle} className="result-icon" />
            <div className="result-text">
              <span className="result-label">{jobData.validationPassed ? 'Validation Passed' : 'Validation Failed'}</span>
              <span className="result-detail">
                {jobData.totalDiscrepancies === 0
                  ? 'No discrepancies found'
                  : `${jobData.totalDiscrepancies} discrepanc${jobData.totalDiscrepancies === 1 ? 'y' : 'ies'} found`}
              </span>
            </div>
          </div>
          {jobData.totalDiscrepancies > 0 && (
            <div className="severity-breakdown">
              {jobData.highSeverityCount > 0 && (
                <div className="severity-badge high">
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                  {jobData.highSeverityCount} High
                </div>
              )}
              {jobData.mediumSeverityCount > 0 && (
                <div className="severity-badge medium">
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                  {jobData.mediumSeverityCount} Medium
                </div>
              )}
              {jobData.lowSeverityCount > 0 && (
                <div className="severity-badge low">
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                  {jobData.lowSeverityCount} Low
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Error Message */}
      {jobData.status === 'failed' && jobData.errorMessage && (
        <div className="qa-error-section">
          <h3>Error</h3>
          <p className="error-message">{jobData.errorMessage}</p>
        </div>
      )}

      {/* Discrepancies */}
      {discrepancies.length > 0 && (
        <div className="qa-discrepancies-section">
          <h2>Discrepancies ({discrepancies.length})</h2>
          <div className="discrepancies-list">
            {discrepancies.map((d, index) => (
              <div key={index} className={`discrepancy-card ${d.severity?.toLowerCase() || 'low'}`}>
                <div className="discrepancy-header">
                  <span className={`severity-tag ${d.severity?.toLowerCase()}`}>
                    {d.severity || 'Unknown'}
                  </span>
                  {d.category && <span className="discrepancy-category">{d.category}</span>}
                </div>
                <p className="discrepancy-issue">{d.issue || '-'}</p>
                {(d.expected || d.actual) && (
                  <div className="discrepancy-values">
                    {d.expected && <div className="expected-value"><strong>Expected:</strong> {d.expected}</div>}
                    {d.actual && <div className="actual-value"><strong>Actual:</strong> {d.actual}</div>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Parsing State (Phase 1 - campaign-processor) */}
      {jobData.status === 'parsing' && isPolling && (
        <div className="qa-processing-section">
          <FontAwesomeIcon icon={faSpinner} spin size="2x" />
          <p>Parsing PDF...</p>
          <span className="processing-hint">The IO / media plan is being processed. This may take a minute.</span>
        </div>
      )}

      {/* Processing State (Phase 2 - qa-assistant) */}
      {jobData.status === 'processing' && isPolling && (
        <div className="qa-processing-section">
          <FontAwesomeIcon icon={faSpinner} spin size="2x" />
          <p>Validation in progress...</p>
          <span className="processing-hint">This may take a few minutes depending on the campaign size.</span>
        </div>
      )}

      {/* Stopped State */}
      {jobData.status === 'stopped' && (
        <div className="qa-pending-section">
          <p>Validation was stopped.</p>
        </div>
      )}

      {/* Pending State */}
      {jobData.status === 'pending' && (
        <div className="qa-pending-section">
          <p>This job is ready to run. Click "Run Validation" to start.</p>
        </div>
      )}
    </div>
  );
};

export default CampaignQADetails;

